/**
 * The two-layer presentation pipeline (doc 17 §1).
 *
 * World and UI are two containers with independent INTEGER scales:
 *
 *   world.scale = S in {2,3,4}   <- this is the zoom control
 *   ui.scale    = U in {1,2,3}   <- never touched by zoom
 *
 * There is deliberately no full-screen render texture. Scaling a container by
 * an integer with nearest filtering, with every child at an integer internal
 * coordinate, is pixel-identical to blitting an offscreen buffer — and costs a
 * few uniforms instead of a 2 MB upload per frame. Post effects that genuinely
 * need the whole frame (vignette, dither wipes) ride as overlay sprites.
 */

import { Application, Container, Sprite, Texture } from 'pixi.js';
import { PixelSurface } from './surface.ts';

/** Upload a finished pixel buffer as a nearest-filtered texture. */
export function surfaceToTexture(s: PixelSurface): Texture {
  const canvas = document.createElement('canvas');
  canvas.width = s.width;
  canvas.height = s.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D context unavailable — cannot build sprite atlases');
  ctx.putImageData(s.toImageData(), 0, 0);
  const tex = Texture.from(canvas);
  tex.source.scaleMode = 'nearest';
  tex.source.autoGenerateMipmaps = false;
  return tex;
}

/** A surface whose pixels change at runtime (the HUD, the minimap). */
export class DynamicSurface {
  surface: PixelSurface;
  readonly sprite: Sprite;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private texture: Texture;

  constructor(width: number, height: number) {
    this.surface = new PixelSurface(width, height);
    this.canvas = document.createElement('canvas');
    this.canvas.width = width;
    this.canvas.height = height;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('2D context unavailable');
    this.ctx = ctx;
    this.texture = Texture.from(this.canvas);
    this.texture.source.scaleMode = 'nearest';
    this.texture.source.autoGenerateMipmaps = false;
    this.sprite = new Sprite(this.texture);
  }

  resize(width: number, height: number): void {
    if (width === this.surface.width && height === this.surface.height) return;
    this.surface = new PixelSurface(width, height);
    this.canvas.width = width;
    this.canvas.height = height;
    this.texture.destroy(true);
    this.texture = Texture.from(this.canvas);
    this.texture.source.scaleMode = 'nearest';
    this.texture.source.autoGenerateMipmaps = false;
    this.sprite.texture = this.texture;
  }

  /** Push the CPU buffer to the GPU. Call once per change, never per frame. */
  upload(): void {
    this.ctx.putImageData(this.surface.toImageData(), 0, 0);
    this.texture.source.update();
  }
}

/** Texture cache keyed by whatever identifies a sprite variant. */
export class TextureCache {
  private map = new Map<string, Texture>();

  get(key: string, make: () => PixelSurface): Texture {
    let t = this.map.get(key);
    if (!t) {
      t = surfaceToTexture(make());
      this.map.set(key, t);
    }
    return t;
  }

  get size(): number {
    return this.map.size;
  }

  /**
   * Doc 18 §6: every texture in here belongs to a GPU context that just went
   * away. Dropping the map is the whole recovery — the next `get` regenerates
   * from the same pure surface functions it did the first time.
   */
  clear(): void {
    this.map.clear();
  }
}

export interface Layers {
  /** Behind everything, untransformed: the water the whole scene hangs in. */
  back: Container;
  app: Application;
  /** Integer-scaled world. Children sit at internal pixel coordinates. */
  world: Container;
  /** Integer-scaled UI. Never resized by zoom. */
  ui: Container;
}

export async function createLayers(canvas: HTMLCanvasElement, background: number): Promise<Layers> {
  const app = new Application();
  await app.init({
    canvas,
    resizeTo: window,
    background,
    antialias: false,
    roundPixels: true,
    autoDensity: true,
    resolution: Math.max(1, Math.floor(window.devicePixelRatio || 1)),
    preference: 'webgl',
  });

  // doc 10: the game happens IN water, not in front of a void. `back` sits
  // behind the camera transform and is painted in screen space, so a zoomed-out
  // view shows ocean past the edge of the map instead of the clear colour.
  const back = new Container();
  const world = new Container();
  const ui = new Container();
  app.stage.addChild(back);
  app.stage.addChild(world);
  app.stage.addChild(ui);
  return { app, back, world, ui };
}
